<template>
  <div class="anomaly-list-page">
    <header class="halloween-record-heading">
      <h1>Liste des anomalies</h1>
      <p>Suivi des anomalies et de leur résolution</p>
    </header>
    <section class="anomaly-summary-grid" aria-label="Résumé des anomalies">
      <article class="anomaly-summary-card">
        <span class="anomaly-summary-icon anomaly-summary-icon--all"><i class="fas fa-layer-group"></i></span>
        <div><strong>{{ filteredAnomalies.length }}</strong><span>Total</span></div>
      </article>
      <article class="anomaly-summary-card">
        <span class="anomaly-summary-icon anomaly-summary-icon--open"><i class="fas fa-hourglass-half"></i></span>
        <div><strong>{{ openCount }}</strong><span>En cours</span></div>
      </article>
      <article class="anomaly-summary-card">
        <span class="anomaly-summary-icon anomaly-summary-icon--closed"><i class="fas fa-check"></i></span>
        <div><strong>{{ closedCount }}</strong><span>Clôturées</span></div>
      </article>
      <article class="anomaly-summary-card">
        <span class="anomaly-summary-icon anomaly-summary-icon--cost"><i class="fas fa-euro-sign"></i></span>
        <div><strong>{{ formatCurrency(totalCost) }}</strong><span>Coût total</span></div>
      </article>
    </section>

    <section class="anomaly-toolbar-card">
      <div class="anomaly-toolbar-main">
        <label class="anomaly-search-field">
          <i class="fas fa-search" aria-hidden="true"></i>
          <input
            v-model.trim="search"
            type="search"
            placeholder="N°, commande, article, client, cause…"
            aria-label="Rechercher une anomalie"
          >
          <button v-if="search" type="button" class="anomaly-clear-search" aria-label="Effacer la recherche" @click="search = ''">
            <i class="fas fa-times"></i>
          </button>
        </label>

        <div class="anomaly-filter-field">
          <span>Statut</span>
          <el-select
            ref="statusFilter"
            v-model="selectedFilter"
            aria-label="Filtrer par statut"
            popper-class="anomaly-filter-popper anomaly-toolbar-popper"
            @focus="closeAdvancedFilterPopups('statusFilter')"
          >
            <el-option label="Toutes les anomalies" value="all"></el-option>
            <el-option label="En cours" value="in_progress"></el-option>
            <el-option label="Clôturées" value="closed"></el-option>
          </el-select>
        </div>

        <div class="anomaly-filter-field anomaly-year-filter">
          <span>Année</span>
          <el-select
            ref="yearFilter"
            v-model="selectedYear"
            aria-label="Filtrer par année de création"
            popper-class="anomaly-filter-popper anomaly-toolbar-popper"
            @focus="closeAdvancedFilterPopups('yearFilter')"
          >
            <el-option label="Toutes" value="all"></el-option>
            <el-option
              v-for="year in availableYears"
              :key="year"
              :label="String(year)"
              :value="String(year)"
            ></el-option>
          </el-select>
        </div>
      </div>

      <div class="anomaly-toolbar-actions">
        <button
          type="button"
          class="btn anomaly-secondary-action anomaly-advanced-toggle"
          :class="{ 'is-active': advancedFiltersOpen || activeAdvancedFilterCount > 0 }"
          :aria-expanded="advancedFiltersOpen ? 'true' : 'false'"
          aria-controls="anomaly-advanced-filters"
          @click="advancedFiltersOpen = !advancedFiltersOpen"
        >
          <i class="fas fa-sliders-h" aria-hidden="true"></i>
          Filtres avancés
          <span v-if="activeAdvancedFilterCount" class="anomaly-filter-count">{{ activeAdvancedFilterCount }}</span>
        </button>
        <button v-if="hasActiveFilters" type="button" class="btn anomaly-secondary-action" @click="resetFilters">
          <i class="fas fa-undo-alt" aria-hidden="true"></i>
          Réinitialiser
        </button>
        <div class="anomaly-view-toggle" role="group" aria-label="Mode d’affichage">
          <button
            type="button"
            :class="{ 'is-active': viewMode === 'cards' }"
            :aria-pressed="viewMode === 'cards' ? 'true' : 'false'"
            title="Affichage en cartes"
            @click="viewMode = 'cards'"
          >
            <i class="fas fa-th-large" aria-hidden="true"></i>
            <span>Cartes</span>
          </button>
          <button
            type="button"
            :class="{ 'is-active': viewMode === 'table' }"
            :aria-pressed="viewMode === 'table' ? 'true' : 'false'"
            title="Affichage en tableau"
            @click="viewMode = 'table'"
          >
            <i class="fas fa-table" aria-hidden="true"></i>
            <span>Tableau</span>
          </button>
        </div>
        <button
          type="button"
          class="btn anomaly-secondary-action anomaly-icon-action"
          :disabled="isExportingAnomalies"
          :title="isExportingAnomalies ? 'Export en cours…' : 'Exporter les anomalies filtrées'"
          :aria-label="isExportingAnomalies ? 'Export en cours' : 'Exporter les anomalies filtrées'"
          @click="exports"
        >
          <i class="fas" :class="isExportingAnomalies ? 'fa-circle-notch fa-spin' : 'fa-file-excel'" aria-hidden="true"></i>
          <span class="sr-only">{{ isExportingAnomalies ? 'Export en cours' : 'Exporter' }}</span>
        </button>
        <button type="button" class="btn anomaly-primary-action anomaly-icon-action" title="Nouvelle anomalie" aria-label="Nouvelle anomalie" @click="add">
          <i class="fas fa-plus" aria-hidden="true"></i>
          <span class="sr-only">Nouvelle anomalie</span>
        </button>
      </div>

      <div v-show="advancedFiltersOpen" id="anomaly-advanced-filters" class="anomaly-advanced-filters">
        <div class="anomaly-advanced-filter">
          <label for="anomaly-customer-filter">Client</label>
          <el-select
            ref="customerFilter"
            id="anomaly-customer-filter"
            v-model="selectedCustomer"
            filterable
            clearable
            popper-class="anomaly-filter-popper"
            placeholder="Tous les clients"
            no-data-text="Aucun client"
            no-match-text="Aucun résultat"
            @focus="closeAdvancedFilterPopups('customerFilter')"
          >
            <el-option v-for="option in customerOptions" :key="option.value" :label="option.label" :value="option.value"></el-option>
          </el-select>
        </div>

        <div class="anomaly-advanced-filter">
          <label for="anomaly-article-filter">Article</label>
          <el-select
            ref="articleFilter"
            id="anomaly-article-filter"
            v-model="selectedArticle"
            filterable
            clearable
            popper-class="anomaly-filter-popper"
            placeholder="Tous les articles"
            no-data-text="Aucun article"
            no-match-text="Aucun résultat"
            @focus="closeAdvancedFilterPopups('articleFilter')"
          >
            <el-option v-for="option in articleOptions" :key="option.value" :label="option.label" :value="option.value"></el-option>
          </el-select>
        </div>

        <div class="anomaly-advanced-filter">
          <label for="anomaly-declarer-filter">Déclarée par</label>
          <el-select
            ref="declarerFilter"
            id="anomaly-declarer-filter"
            v-model="selectedDeclarer"
            filterable
            clearable
            popper-class="anomaly-filter-popper"
            placeholder="Tous les déclarants"
            no-data-text="Aucun déclarant"
            no-match-text="Aucun résultat"
            @focus="closeAdvancedFilterPopups('declarerFilter')"
          >
            <el-option v-for="option in declarerOptions" :key="option.value" :label="option.label" :value="option.value"></el-option>
          </el-select>
        </div>

        <div class="anomaly-advanced-filter">
          <label for="anomaly-director-filter">Visa direction</label>
          <el-select
            ref="directorFilter"
            id="anomaly-director-filter"
            v-model="selectedDirector"
            filterable
            clearable
            popper-class="anomaly-filter-popper"
            placeholder="Tous les responsables"
            no-data-text="Aucun responsable"
            no-match-text="Aucun résultat"
            @focus="closeAdvancedFilterPopups('directorFilter')"
          >
            <el-option v-for="option in directorOptions" :key="option.value" :label="option.label" :value="option.value"></el-option>
          </el-select>
        </div>

        <div class="anomaly-advanced-filter">
          <label for="anomaly-department-filter">Service</label>
          <el-select
            ref="departmentFilter"
            id="anomaly-department-filter"
            v-model="selectedDepartment"
            filterable
            clearable
            popper-class="anomaly-filter-popper"
            placeholder="Tous les services"
            no-data-text="Aucun service"
            no-match-text="Aucun résultat"
            @focus="closeAdvancedFilterPopups('departmentFilter')"
          >
            <el-option v-for="option in departmentOptions" :key="option.value" :label="option.label" :value="option.value"></el-option>
          </el-select>
        </div>

        <div class="anomaly-advanced-filter">
          <label for="anomaly-cost-filter">Coût</label>
          <el-select
            ref="costFilter"
            id="anomaly-cost-filter"
            v-model="selectedCost"
            clearable
            popper-class="anomaly-filter-popper"
            placeholder="Tous les coûts"
            @focus="closeAdvancedFilterPopups('costFilter')"
          >
            <el-option label="Avec un coût" value="with_cost"></el-option>
            <el-option label="Sans coût" value="without_cost"></el-option>
          </el-select>
        </div>

        <div class="anomaly-advanced-filter anomaly-period-filter">
          <label for="anomaly-period-filter">Période de réception</label>
          <el-date-picker
            ref="periodFilter"
            id="anomaly-period-filter"
            v-model="selectedPeriod"
            type="daterange"
            format="dd/MM/yyyy"
            value-format="yyyy-MM-dd"
            range-separator="au"
            start-placeholder="Date de début"
            end-placeholder="Date de fin"
            popper-class="anomaly-filter-popper"
            :picker-options="periodPickerOptions"
            :clearable="true"
            @focus="closeAdvancedFilterPopups('periodFilter')"
          ></el-date-picker>
        </div>
      </div>
    </section>

    <div v-if="isLoading && viewMode === 'table'" class="anomaly-table-shell" aria-label="Chargement des anomalies" aria-busy="true">
      <div class="anomaly-table-scroll">
        <table class="anomaly-table anomaly-table--loading">
          <thead>
            <tr><th v-for="column in tableColumns" :key="column" scope="col">{{ column }}</th></tr>
          </thead>
          <tbody>
            <tr v-for="row in 8" :key="'table-skeleton-' + row">
              <td v-for="column in tableColumns.length" :key="'table-skeleton-' + row + '-' + column">
                <span class="anomaly-skeleton anomaly-table-skeleton"></span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else-if="isLoading" class="row anomaly-card-grid" aria-label="Chargement des anomalies" aria-busy="true">
      <div v-for="index in 8" :key="'skeleton-' + index" class="col-xl-3 col-lg-6 col-md-6 anomaly-card-column">
        <article class="anomaly-result-card anomaly-result-card--skeleton">
          <header class="anomaly-card-header">
            <span class="anomaly-skeleton anomaly-skeleton--reference"></span>
            <span class="anomaly-skeleton anomaly-skeleton--badge"></span>
          </header>
          <div class="anomaly-card-content">
            <span class="anomaly-skeleton anomaly-skeleton--title"></span>
            <span class="anomaly-skeleton anomaly-skeleton--subtitle"></span>
            <span class="anomaly-skeleton anomaly-skeleton--fact"></span>
            <span class="anomaly-skeleton anomaly-skeleton--fact"></span>
            <span class="anomaly-skeleton anomaly-skeleton--description"></span>
          </div>
          <footer class="anomaly-card-footer">
            <span v-for="action in 3" :key="action" class="anomaly-skeleton anomaly-skeleton--action"></span>
          </footer>
        </article>
      </div>
    </div>

    <div v-else-if="loadError" class="anomaly-state-card anomaly-state-card--error">
      <span><i class="fas fa-exclamation-triangle"></i></span>
      <h3>Impossible de charger les anomalies</h3>
      <p>Une erreur est survenue pendant la récupération des données.</p>
      <button type="button" class="btn anomaly-primary-action" @click="fetchAnomalies">Réessayer</button>
    </div>

    <div v-else-if="filteredAnomalies.length === 0" class="anomaly-state-card">
      <span><i class="fas fa-search"></i></span>
      <h3>Aucune anomalie trouvée</h3>
      <p>Modifiez les filtres ou la recherche pour afficher d’autres résultats.</p>
      <button v-if="hasActiveFilters" type="button" class="btn anomaly-secondary-action" @click="resetFilters">Réinitialiser les filtres</button>
    </div>

    <div v-else-if="viewMode === 'table'" class="anomaly-table-shell">
      <div class="anomaly-table-scroll" role="region" aria-label="Liste des anomalies en tableau" tabindex="0">
        <table class="anomaly-table">
          <caption class="sr-only">Liste des anomalies</caption>
          <thead>
            <tr>
              <th scope="col">Anomalie</th>
              <th scope="col">Statut</th>
              <th scope="col">Article / client</th>
              <th scope="col">Commande</th>
              <th scope="col">Réception</th>
              <th scope="col">Déclarée par</th>
              <th scope="col">Service</th>
              <th scope="col">Description / cause</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="anomaly in filteredAnomalies" :key="'table-' + anomaly.id">
              <td class="anomaly-table-reference">
                <button type="button" @click="showAnomalyDetails(anomaly)">{{ valueOr(anomaly.num) }}</button>
                <small v-if="hasCost(anomaly)">{{ formatCurrency(anomaly.cost) }}</small>
              </td>
              <td>
                <span class="anomaly-badge" :class="anomaly.archived_at ? 'anomaly-badge--closed' : 'anomaly-badge--open'">
                  <i class="fas" :class="anomaly.archived_at ? 'fa-check-circle' : 'fa-clock'"></i>
                  {{ anomaly.archived_at ? 'Clôturée' : 'En cours' }}
                </span>
              </td>
              <td class="anomaly-table-product">
                <strong :title="articleName(anomaly)">{{ articleName(anomaly) }}</strong>
                <small :title="customerName(anomaly)">{{ customerName(anomaly) }}</small>
              </td>
              <td class="anomaly-table-command">
                <strong>{{ valueOr(anomaly.command_number) }}</strong>
                <small>{{ formatQuantity(anomaly.quantity) }}</small>
              </td>
              <td class="anomaly-table-date">
                <strong>{{ formatShortDate(anomaly.received_at) }}</strong>
                <small>Créée le {{ formatShortDate(anomaly.created_at) }}</small>
              </td>
              <td>{{ declarerName(anomaly) }}</td>
              <td>{{ valueOr(anomaly.department, 'Non renseigné') }}</td>
              <td class="anomaly-table-description"><span :title="descriptionText(anomaly)">{{ descriptionText(anomaly) }}</span></td>
              <td>
                <div class="anomaly-card-actions anomaly-table-actions" aria-label="Actions de l’anomalie">
                  <button type="button" title="Voir le détail" aria-label="Voir le détail" @click="showAnomalyDetails(anomaly)"><i class="fas fa-eye"></i></button>
                  <button type="button" title="Modifier" aria-label="Modifier" @click="updateAnomaly(anomaly.id)"><i class="fas fa-pen"></i></button>
                  <button type="button" class="anomaly-action--danger" title="Supprimer" aria-label="Supprimer" @click="confirmDelete(anomaly)"><i class="fas fa-trash"></i></button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else class="row anomaly-card-grid">
      <div v-for="anomaly in filteredAnomalies" :key="anomaly.id" class="col-xl-3 col-lg-6 col-md-6 anomaly-card-column">
        <article class="anomaly-result-card">
          <header class="anomaly-card-header">
            <button type="button" class="anomaly-reference" @click="showAnomalyDetails(anomaly)">{{ valueOr(anomaly.num) }}</button>
            <div class="anomaly-card-badges">
              <span v-if="hasCost(anomaly)" class="anomaly-badge anomaly-badge--cost">{{ formatCurrency(anomaly.cost) }}</span>
              <span class="anomaly-badge" :class="anomaly.archived_at ? 'anomaly-badge--closed' : 'anomaly-badge--open'">
                <i class="fas" :class="anomaly.archived_at ? 'fa-check-circle' : 'fa-clock'"></i>
                {{ anomaly.archived_at ? 'Clôturée' : 'En cours' }}
              </span>
            </div>
          </header>

          <div class="anomaly-card-content">
            <div class="anomaly-card-product">
              <h3 :title="articleName(anomaly)">{{ articleName(anomaly) }}</h3>
              <span class="anomaly-overline">{{ customerName(anomaly) }}</span>
            </div>

            <div class="anomaly-card-facts">
              <div class="anomaly-card-fact anomaly-card-fact--wide">
                <span class="anomaly-fact-icon"><i class="fas fa-calendar-alt"></i></span>
                <span><small>Date de réception</small><strong>{{ formatShortDate(anomaly.received_at) }}</strong></span>
              </div>
              <div class="anomaly-card-fact">
                <span class="anomaly-fact-icon"><i class="fas fa-file-invoice"></i></span>
                <span><small>Commande</small><strong>{{ valueOr(anomaly.command_number) }}</strong></span>
              </div>
              <div class="anomaly-card-fact">
                <span class="anomaly-fact-icon"><i class="fas fa-boxes"></i></span>
                <span><small>Quantité</small><strong>{{ valueOr(anomaly.quantity, '0') }}</strong></span>
              </div>
            </div>

            <div class="anomaly-description-box">
              <small>Description / cause</small>
              <p :title="descriptionText(anomaly)">{{ descriptionText(anomaly) }}</p>
            </div>

            <div class="anomaly-card-history">
              <div>
                <span class="anomaly-history-icon anomaly-history-icon--created"><i class="fas fa-plus"></i></span>
                <span><small>Déclarée le {{ formatShortDate(anomaly.created_at) }}</small><strong>par {{ declarerName(anomaly) }}</strong></span>
              </div>
              <div v-if="anomaly.archived_at">
                <span class="anomaly-history-icon anomaly-history-icon--closed"><i class="fas fa-check"></i></span>
                <span><small>Clôturée le {{ formatShortDate(anomaly.archived_at) }}</small><strong>par {{ archiverName(anomaly) }}</strong></span>
              </div>
              <div v-else>
                <span class="anomaly-history-icon anomaly-history-icon--department"><i class="fas fa-building"></i></span>
                <span><small>Service</small><strong>{{ valueOr(anomaly.department, 'Non renseigné') }}</strong></span>
              </div>
            </div>
          </div>

          <footer class="anomaly-card-footer">
            <div class="anomaly-card-actions" aria-label="Actions de l’anomalie">
              <button type="button" title="Voir le détail" aria-label="Voir le détail" @click="showAnomalyDetails(anomaly)"><i class="fas fa-eye"></i></button>
              <button type="button" title="Modifier" aria-label="Modifier" @click="updateAnomaly(anomaly.id)"><i class="fas fa-pen"></i></button>
              <button type="button" class="anomaly-action--danger" title="Supprimer" aria-label="Supprimer" @click="confirmDelete(anomaly)"><i class="fas fa-trash"></i></button>
            </div>
          </footer>
        </article>
      </div>
    </div>

    <el-dialog
      :visible.sync="dialogVisible"
      custom-class="anomaly-detail-dialog"
      width="760px"
      append-to-body
      :before-close="handleClose"
    >
      <div slot="title" class="anomaly-dialog-title">
        <span class="anomaly-dialog-icon"><i class="fas fa-exclamation-triangle"></i></span>
        <div>
          <strong>Détail de l’anomalie</strong>
          <small v-if="selectedAnomaly">{{ selectedAnomaly.num }}</small>
        </div>
      </div>

      <div v-if="selectedAnomaly" class="anomaly-detail-grid">
        <section class="anomaly-detail-section">
          <h3>Informations générales</h3>
          <dl>
            <div><dt>Date de réception</dt><dd>{{ formatShortDate(selectedAnomaly.received_at) }}</dd></div>
            <div><dt>Déclarée par</dt><dd>{{ declarerName(selectedAnomaly) }}</dd></div>
            <div><dt>Service</dt><dd>{{ valueOr(selectedAnomaly.department, 'Non renseigné') }}</dd></div>
            <div><dt>Statut</dt><dd>{{ selectedAnomaly.archived_at ? 'Clôturée' : 'En cours' }}</dd></div>
          </dl>
        </section>
        <section class="anomaly-detail-section">
          <h3>Commande</h3>
          <dl>
            <div><dt>N° de commande</dt><dd>{{ valueOr(selectedAnomaly.command_number) }}</dd></div>
            <div><dt>Article</dt><dd>{{ articleName(selectedAnomaly) }}</dd></div>
            <div><dt>Quantité</dt><dd>{{ valueOr(selectedAnomaly.quantity, '0') }}</dd></div>
            <div><dt>Client</dt><dd>{{ customerName(selectedAnomaly) }}</dd></div>
          </dl>
        </section>
        <section class="anomaly-detail-section">
          <h3>Coût et validation</h3>
          <dl>
            <div><dt>Coût</dt><dd>{{ formatCurrency(selectedAnomaly.cost || 0) }}</dd></div>
            <div><dt>Détail du coût</dt><dd>{{ valueOr(selectedAnomaly.cost_detail) }}</dd></div>
            <div><dt>Visa direction</dt><dd>{{ directorName(selectedAnomaly) }}</dd></div>
            <div><dt>Clôturée par</dt><dd>{{ selectedAnomaly.archived_at ? archiverName(selectedAnomaly) : 'Non clôturée' }}</dd></div>
          </dl>
        </section>
        <section class="anomaly-detail-section">
          <h3>Clôture</h3>
          <dl>
            <div><dt>Date de clôture</dt><dd>{{ formatShortDate(selectedAnomaly.archived_at) }}</dd></div>
            <div class="anomaly-detail-long"><dt>Action réalisée</dt><dd>{{ valueOr(selectedAnomaly.comments) }}</dd></div>
          </dl>
        </section>
        <section class="anomaly-detail-section anomaly-detail-section--wide">
          <h3>Observations et cause</h3>
          <dl>
            <div class="anomaly-detail-long"><dt>Cause</dt><dd>{{ valueOr(selectedAnomaly.cause) }}</dd></div>
            <div class="anomaly-detail-long"><dt>Observation interne</dt><dd>{{ valueOr(selectedAnomaly.intern_observation) }}</dd></div>
          </dl>
        </section>
      </div>

      <span slot="footer" class="anomaly-dialog-footer">
        <el-button @click="handleClose">Fermer</el-button>
        <el-button v-if="selectedAnomaly" type="primary" @click="updateAnomaly(selectedAnomaly.id)">Modifier</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { Button, DatePicker, Dialog, Option, Select } from 'element-ui'
import Swal from 'sweetalert2'

const ANOMALY_FILTERS_STORAGE_PREFIX = 'process-v2.anomalies-list.filters.v1'
const ANOMALY_STATUS_FILTERS = ['all', 'in_progress', 'closed']
const ANOMALY_COST_FILTERS = ['with_cost', 'without_cost']

export default {
  name: 'AnomaliesList',
  components: {
    [Button.name]: Button,
    [DatePicker.name]: DatePicker,
    [Dialog.name]: Dialog,
    [Option.name]: Option,
    [Select.name]: Select
  },
  data() {
    return {
      anomalies: [],
      search: '',
      selectedFilter: 'all',
      selectedYear: 'all',
      selectedCustomer: null,
      selectedArticle: null,
      selectedDeclarer: null,
      selectedDirector: null,
      selectedDepartment: null,
      selectedCost: null,
      selectedPeriod: null,
      periodPickerOptions: { firstDayOfWeek: 1 },
      viewMode: 'cards',
      advancedFiltersOpen: false,
      isLoading: true,
      loadError: false,
      isExportingAnomalies: false,
      dialogVisible: false,
      selectedAnomaly: null,
      authenticatedUser: null,
      tableColumns: ['Anomalie', 'Statut', 'Article / client', 'Commande', 'Réception', 'Déclarée par', 'Service', 'Description / cause', 'Actions']
    }
  },
  computed: {
    filteredAnomalies() {
      const query = this.normalize(this.search)

      return this.anomalies.filter(anomaly => {
        const matchesStatus = this.selectedFilter === 'all'
          || (this.selectedFilter === 'in_progress' && !anomaly.archived_at)
          || (this.selectedFilter === 'closed' && !!anomaly.archived_at)
        const matchesYear = this.selectedYear === 'all'
          || String(this.dateYear(anomaly.created_at)) === this.selectedYear
        const matchesCustomer = !this.selectedCustomer
          || String(anomaly.customer && anomaly.customer.id) === this.selectedCustomer
        const matchesArticle = !this.selectedArticle || String(anomaly.article_name) === this.selectedArticle
        const matchesDeclarer = !this.selectedDeclarer
          || String(anomaly.declared_by && anomaly.declared_by.id) === this.selectedDeclarer
        const matchesDirector = !this.selectedDirector
          || String(anomaly.directed_by && anomaly.directed_by.id) === this.selectedDirector
        const matchesDepartment = !this.selectedDepartment || String(anomaly.department) === this.selectedDepartment
        const matchesCost = !this.selectedCost
          || (this.selectedCost === 'with_cost' && this.hasCost(anomaly))
          || (this.selectedCost === 'without_cost' && !this.hasCost(anomaly))
        const matchesPeriod = this.matchesSelectedPeriod(anomaly.received_at)

        if (!matchesStatus || !matchesYear || !matchesCustomer || !matchesArticle || !matchesDeclarer
          || !matchesDirector || !matchesDepartment || !matchesCost || !matchesPeriod) return false

        if (!query) return true

        const searchable = [
          anomaly.num,
          anomaly.command_number,
          anomaly.article_name,
          anomaly.customer && anomaly.customer.name,
          anomaly.declared_by && anomaly.declared_by.name,
          anomaly.directed_by && anomaly.directed_by.name,
          anomaly.archived_by && anomaly.archived_by.name,
          anomaly.department,
          anomaly.intern_observation,
          anomaly.cause,
          anomaly.comments,
          anomaly.cost_detail,
          anomaly.archived_at ? 'clôturée fermée' : 'en cours ouverte',
          this.formatShortDate(anomaly.received_at),
          this.formatShortDate(anomaly.created_at)
        ].map(this.normalize).join(' ')

        return searchable.indexOf(query) !== -1
      })
    },
    availableYears() {
      const years = this.anomalies.map(anomaly => this.dateYear(anomaly.created_at)).filter(year => !!year)
      return Array.from(new Set(years)).sort((a, b) => b - a)
    },
    customerOptions() {
      return this.relationFilterOptions('customer')
    },
    declarerOptions() {
      return this.relationFilterOptions('declared_by')
    },
    directorOptions() {
      return this.relationFilterOptions('directed_by')
    },
    articleOptions() {
      return this.primitiveFilterOptions('article_name')
    },
    departmentOptions() {
      return this.primitiveFilterOptions('department')
    },
    openCount() {
      return this.filteredAnomalies.filter(anomaly => !anomaly.archived_at).length
    },
    closedCount() {
      return this.filteredAnomalies.filter(anomaly => !!anomaly.archived_at).length
    },
    totalCost() {
      return this.filteredAnomalies.reduce((total, anomaly) => total + (Number(anomaly.cost) || 0), 0)
    },
    activeAdvancedFilterCount() {
      return [
        this.selectedCustomer,
        this.selectedArticle,
        this.selectedDeclarer,
        this.selectedDirector,
        this.selectedDepartment,
        this.selectedCost,
        Array.isArray(this.selectedPeriod) && this.selectedPeriod.length === 2
      ].filter(Boolean).length
    },
    hasActiveFilters() {
      return !!this.search || this.selectedFilter !== 'all' || this.selectedYear !== 'all'
        || this.activeAdvancedFilterCount > 0
    }
  },
  watch: {
    search: 'persistFilters',
    selectedFilter: 'persistFilters',
    selectedYear: 'persistFilters',
    selectedCustomer: 'persistFilters',
    selectedArticle: 'persistFilters',
    selectedDeclarer: 'persistFilters',
    selectedDirector: 'persistFilters',
    selectedDepartment: 'persistFilters',
    selectedCost: 'persistFilters',
    viewMode: 'persistFilters',
    selectedPeriod: {
      handler: 'persistFilters',
      deep: true
    }
  },
  mounted() {
    this.handleAuthenticatedUser()
    this.fetchAnomalies().then(() => this.openAnomalyFromRoute())
    if (window.EventBus) window.EventBus.$on('isAutenticated', this.handleAuthenticatedUser)
  },
  beforeDestroy() {
    if (window.EventBus) window.EventBus.$off('isAutenticated', this.handleAuthenticatedUser)
  },
  methods: {
    handleAuthenticatedUser() {
      this.authenticatedUser = window.App || null
      this.restoreFilters()
    },
    filterStorageKey() {
      const user = this.authenticatedUser || window.App
      if (!user || user.id === null || user.id === undefined) return null
      return ANOMALY_FILTERS_STORAGE_PREFIX + '.user-' + user.id
    },
    persistFilters() {
      const storageKey = this.filterStorageKey()
      if (!storageKey) return

      try {
        window.localStorage.setItem(storageKey, JSON.stringify({
          search: this.search,
          selectedFilter: this.selectedFilter,
          selectedYear: this.selectedYear,
          selectedCustomer: this.selectedCustomer,
          selectedArticle: this.selectedArticle,
          selectedDeclarer: this.selectedDeclarer,
          selectedDirector: this.selectedDirector,
          selectedDepartment: this.selectedDepartment,
          selectedCost: this.selectedCost,
          selectedPeriod: this.selectedPeriod,
          viewMode: this.viewMode
        }))
      } catch (error) {
        // Le stockage local ne doit jamais empêcher l’utilisation de la liste.
      }
    },
    restoreFilters() {
      const storageKey = this.filterStorageKey()
      if (!storageKey) return

      try {
        const savedFilters = window.localStorage.getItem(storageKey)
        if (!savedFilters) return
        const filters = JSON.parse(savedFilters)
        const selectedYear = String(filters.selectedYear || '')
        const optionalValue = value => {
          return typeof value === 'string' || typeof value === 'number' ? String(value) : null
        }

        if (typeof filters.search === 'string') this.search = filters.search
        if (ANOMALY_STATUS_FILTERS.indexOf(filters.selectedFilter) !== -1) this.selectedFilter = filters.selectedFilter
        if (selectedYear === 'all' || /^\d{4}$/.test(selectedYear)) this.selectedYear = selectedYear
        this.selectedCustomer = optionalValue(filters.selectedCustomer)
        this.selectedArticle = optionalValue(filters.selectedArticle)
        this.selectedDeclarer = optionalValue(filters.selectedDeclarer)
        this.selectedDirector = optionalValue(filters.selectedDirector)
        this.selectedDepartment = optionalValue(filters.selectedDepartment)
        this.selectedCost = ANOMALY_COST_FILTERS.indexOf(filters.selectedCost) !== -1 ? filters.selectedCost : null
        if (['cards', 'table'].indexOf(filters.viewMode) !== -1) this.viewMode = filters.viewMode

        if (Array.isArray(filters.selectedPeriod)
          && filters.selectedPeriod.length === 2
          && filters.selectedPeriod.every(date => /^\d{4}-\d{2}-\d{2}$/.test(date))) {
          this.selectedPeriod = filters.selectedPeriod.slice()
        } else {
          this.selectedPeriod = null
        }

        this.advancedFiltersOpen = this.activeAdvancedFilterCount > 0
      } catch (error) {
        // Une préférence invalide ne doit jamais empêcher l’affichage de la liste.
      }
    },
    async fetchAnomalies() {
      this.isLoading = true
      this.loadError = false

      try {
        const endpoint = process.env.VUE_APP_API_BASE_URL + 'anomalies'
        const firstResponse = await axios.get(endpoint, { params: { page: 1, per_page: 100 } })
        const firstPage = firstResponse.data && firstResponse.data.anomalies
        let loaded = Array.isArray(firstPage) ? firstPage.slice() : ((firstPage && firstPage.data) || []).slice()
        const lastPage = Number(firstPage && firstPage.last_page) || 1

        if (lastPage > 1) {
          const requests = []
          for (let page = 2; page <= lastPage; page += 1) {
            requests.push(axios.get(endpoint, { params: { page, per_page: 100 } }))
          }
          const responses = await Promise.all(requests)
          responses.forEach(response => {
            const responsePage = response.data && response.data.anomalies
            const pageAnomalies = Array.isArray(responsePage) ? responsePage : ((responsePage && responsePage.data) || [])
            loaded = loaded.concat(pageAnomalies)
          })
        }

        const anomaliesById = {}
        loaded.forEach(anomaly => {
          if (!anomaly || anomaly.id === null || anomaly.id === undefined) return
          anomaliesById[anomaly.id] = anomaly
        })
        this.anomalies = Object.keys(anomaliesById)
          .map(id => anomaliesById[id])
          .sort((a, b) => this.dateTimestamp(b.created_at) - this.dateTimestamp(a.created_at))
      } catch (error) {
        this.loadError = true
      }

      this.isLoading = false
    },
    closeAdvancedFilterPopups(activeRef) {
      const refs = ['statusFilter', 'yearFilter', 'customerFilter', 'articleFilter', 'declarerFilter',
        'directorFilter', 'departmentFilter', 'costFilter', 'periodFilter']
      refs.forEach(refName => {
        const filter = this.$refs[refName]
        if (refName === activeRef || !filter) return
        if (typeof filter.handleClose === 'function') filter.handleClose()
        else if (typeof filter.blur === 'function') filter.blur()
      })
    },
    normalize(value) {
      return String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    },
    relationFilterOptions(relationName) {
      const optionsById = {}
      this.anomalies.forEach(anomaly => {
        const relation = anomaly[relationName]
        if (!relation || typeof relation !== 'object' || relation.id === null || relation.id === undefined) return
        const value = String(relation.id)
        if (!optionsById[value]) optionsById[value] = { value, label: relation.name || value }
      })
      return Object.keys(optionsById)
        .map(id => optionsById[id])
        .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }))
    },
    primitiveFilterOptions(property) {
      const values = this.anomalies
        .map(anomaly => anomaly[property])
        .filter(value => value !== null && value !== undefined && value !== '')
        .map(value => String(value))
      return Array.from(new Set(values))
        .sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }))
        .map(value => ({ value, label: value }))
    },
    matchesSelectedPeriod(date) {
      if (!Array.isArray(this.selectedPeriod) || this.selectedPeriod.length !== 2) return true
      const dateKey = this.filterDateKey(date)
      return !!dateKey && dateKey >= this.selectedPeriod[0] && dateKey <= this.selectedPeriod[1]
    },
    filterDateKey(date) {
      if (!date) return null
      const parsed = new Date(date)
      if (Number.isNaN(parsed.getTime())) return null
      const year = parsed.getFullYear()
      const month = String(parsed.getMonth() + 1).padStart(2, '0')
      const day = String(parsed.getDate()).padStart(2, '0')
      return year + '-' + month + '-' + day
    },
    dateTimestamp(date) {
      const parsed = new Date(date)
      return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime()
    },
    dateYear(date) {
      if (!date) return null
      const parsed = new Date(date)
      return Number.isNaN(parsed.getTime()) ? null : parsed.getFullYear()
    },
    formatShortDate(date) {
      if (!date) return '—'
      const parsed = new Date(date)
      return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString('fr-FR')
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(Number(value) || 0)
    },
    formatQuantity(quantity) {
      return 'Quantité : ' + (quantity !== null && quantity !== undefined ? quantity : '—')
    },
    valueOr(value, fallback) {
      return value !== null && value !== undefined && value !== '' ? value : (fallback || '—')
    },
    hasCost(anomaly) {
      return anomaly && Number(anomaly.cost) !== 0
    },
    articleName(anomaly) {
      return anomaly && anomaly.article_name ? anomaly.article_name : 'Article non spécifié'
    },
    customerName(anomaly) {
      return anomaly && anomaly.customer && anomaly.customer.name ? anomaly.customer.name : 'Client non spécifié'
    },
    declarerName(anomaly) {
      return anomaly && anomaly.declared_by && anomaly.declared_by.name ? anomaly.declared_by.name : 'Inconnu'
    },
    directorName(anomaly) {
      return anomaly && anomaly.directed_by && anomaly.directed_by.name ? anomaly.directed_by.name : 'Non spécifié'
    },
    archiverName(anomaly) {
      return anomaly && anomaly.archived_by && anomaly.archived_by.name ? anomaly.archived_by.name : 'Inconnu'
    },
    descriptionText(anomaly) {
      if (!anomaly) return 'Non renseignée'
      return anomaly.intern_observation || anomaly.cause || 'Non renseignée'
    },
    resetFilters() {
      this.search = ''
      this.selectedFilter = 'all'
      this.selectedYear = 'all'
      this.selectedCustomer = null
      this.selectedArticle = null
      this.selectedDeclarer = null
      this.selectedDirector = null
      this.selectedDepartment = null
      this.selectedCost = null
      this.selectedPeriod = null
      this.$nextTick(this.persistFilters)
    },
    add() {
      this.$router.push({ name: 'Anomalies Form' })
    },
    updateAnomaly(id) {
      this.dialogVisible = false
      this.$router.push({ name: 'Anomalies Form', params: { id } })
    },
    showAnomalyDetails(anomaly) {
      this.selectedAnomaly = anomaly
      this.dialogVisible = true

      if (String(this.$route.params.id || '') !== String(anomaly.id)) {
        this.$router.replace({ name: 'Anomalies List', params: { id: anomaly.id } }, () => {}, () => {})
      }
    },
    openAnomalyFromRoute() {
      const requestedId = this.$route.params.id
      if (requestedId === undefined) return

      const anomaly = this.anomalies.find(item => String(item.id) === String(requestedId))
      if (anomaly) this.showAnomalyDetails(anomaly)
    },
    handleClose() {
      this.dialogVisible = false
      this.selectedAnomaly = null

      if (this.$route.params.id !== undefined) {
        this.$router.replace({ name: 'Anomalies List' }, () => {}, () => {})
      }
    },
    confirmDelete(anomaly) {
      Swal.fire({
        title: 'Êtes-vous sûr ?',
        text: 'Cette action est irréversible !',
        type: 'warning',
        showCancelButton: true,
        confirmButtonClass: 'btn btn-danger btn-fill',
        cancelButtonClass: 'btn btn-secondary btn-fill',
        cancelButtonText: 'Retour',
        confirmButtonText: 'Supprimer quand même !',
        buttonsStyling: false
      }).then(result => {
        if (result.value) this.deleteAnomaly(anomaly.id)
      })
    },
    async deleteAnomaly(id) {
      try {
        await axios.delete(process.env.VUE_APP_API_BASE_URL + 'anomalies/' + id)
        this.anomalies = this.anomalies.filter(anomaly => anomaly.id !== id)
        Swal.fire('Supprimé !', 'L’anomalie a été supprimée avec succès.', 'success')
      } catch (error) {
        Swal.fire('Erreur', 'Une erreur est survenue lors de la suppression.', 'error')
      }
    },
    exports() {
      if (this.isExportingAnomalies) return
      const ids = this.filteredAnomalies.map(anomaly => anomaly.id).filter(id => id !== null && id !== undefined)

      if (!ids.length) {
        this.$notify({ message: 'Aucune anomalie à exporter avec les filtres actuels', type: 'warning', verticalAlign: 'bottom' })
        return
      }

      this.isExportingAnomalies = true
      axios({
        url: process.env.VUE_APP_API_BASE_URL + 'anomalies/export',
        method: 'POST',
        responseType: 'blob',
        data: { filter: this.selectedFilter, ids }
      }).then(response => {
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = 'anomalies.xlsx'
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        const label = ids.length > 1 ? 'anomalies ont été exportées' : 'anomalie a été exportée'
        this.$notify({ message: ids.length + ' ' + label, type: 'success', verticalAlign: 'bottom' })
      }).catch(() => {
        this.$notify({ message: 'L’export des anomalies a échoué', type: 'danger', verticalAlign: 'bottom' })
      }).then(() => {
        this.isExportingAnomalies = false
      })
    }
  }
}
</script>

<style>
.anomaly-list-page {
  --anomaly-primary: #f96332;
  --anomaly-primary-dark: #dc4c1d;
  --anomaly-ink: #233044;
  --anomaly-muted: #738096;
  --anomaly-border: #e6eaf0;
  --anomaly-surface: #fff;
  padding-bottom: 24px;
  position: relative;
}

.anomaly-summary-grid { display: grid; gap: 14px; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-bottom: 18px; }
.anomaly-summary-card { align-items: center; background: var(--anomaly-surface); border: 1px solid var(--anomaly-border); border-radius: 12px; box-shadow: 0 5px 16px rgba(36, 48, 68, .045); display: flex; gap: 13px; min-height: 78px; padding: 14px 16px; }
.anomaly-summary-icon { align-items: center; border-radius: 10px; display: inline-flex; flex: 0 0 44px; height: 44px; justify-content: center; width: 44px; }
.anomaly-summary-icon--all { background: #edf3ff; color: #3d6dcc; }
.anomaly-summary-icon--open { background: #fff5df; color: #b87a06; }
.anomaly-summary-icon--closed { background: #eaf8ef; color: #26834b; }
.anomaly-summary-icon--cost { background: #fff0ec; color: #d95528; }
.anomaly-summary-card div { display: flex; flex-direction: column; min-width: 0; }
.anomaly-summary-card strong { color: var(--anomaly-ink); font-size: 22px; line-height: 1.1; overflow: hidden; text-overflow: ellipsis; }
.anomaly-summary-card span:last-child { color: var(--anomaly-muted); font-size: 11px; margin-top: 4px; }

.anomaly-toolbar-card { align-items: center; background: #fff; border: 1px solid var(--anomaly-border); border-radius: 12px; box-shadow: 0 5px 18px rgba(36, 48, 68, .045); display: flex; flex-wrap: wrap; gap: 16px; justify-content: space-between; margin-bottom: 22px; padding: 13px; }
.anomaly-toolbar-main, .anomaly-toolbar-actions { align-items: center; display: flex; }
.anomaly-toolbar-main { flex: 1; gap: 11px; min-width: 0; }
.anomaly-toolbar-actions { gap: 8px; }
.anomaly-primary-action, .anomaly-secondary-action { align-items: center; border-radius: 8px; box-shadow: none; display: inline-flex; font-size: 11px; font-weight: 600; gap: 7px; height: 40px; justify-content: center; margin: 0; padding: 0 13px; text-transform: none; }
.anomaly-primary-action { background: var(--anomaly-primary); border: 1px solid var(--anomaly-primary); color: #fff; }
.anomaly-primary-action:hover, .anomaly-primary-action:focus { background: var(--anomaly-primary-dark); border-color: var(--anomaly-primary-dark); color: #fff; }
.anomaly-secondary-action { background: #fff; border: 1px solid var(--anomaly-border); color: #526078; }
.anomaly-secondary-action:hover, .anomaly-secondary-action:focus { background: #fff7f4; border-color: #f8b49e; color: var(--anomaly-primary-dark); }
.anomaly-search-field { align-items: center; background: #fff; border: 1px solid #d8dde5; border-radius: 8px; display: flex; flex: 1; height: 40px; margin: 0; min-width: 230px; position: relative; }
.anomaly-search-field:focus-within { border-color: var(--anomaly-primary); }
.anomaly-search-field > i { color: #8b95a5; font-size: 12px; left: 12px; pointer-events: none; position: absolute; }
.anomaly-search-field input { background: transparent; border: 0; color: var(--anomaly-ink); flex: 1; font-size: 12px; height: 38px; min-width: 0; outline: none; padding: 0 34px; width: 100%; }
.anomaly-clear-search { background: none; border: 0; color: #9ba5b5; cursor: pointer; padding: 5px; position: absolute; right: 6px; }
.anomaly-filter-field { align-items: center; background: #fff; border: 1px solid #d8dde5; border-radius: 8px; display: flex; height: 40px; min-width: 190px; padding: 0; position: relative; }
.anomaly-filter-field > span { color: #8b95a5; flex: 0 0 auto; font-size: 9px; font-weight: 600; padding-left: 11px; text-transform: uppercase; }
.anomaly-filter-field .el-select { flex: 1; min-width: 0; }
.anomaly-filter-field .el-input { height: 40px !important; margin: 0 !important; }
.anomaly-filter-field .el-input__inner { background: transparent !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; color: #2c2c2c !important; font-size: 12px; font-weight: 400 !important; height: 38px !important; line-height: 38px !important; margin: 0 !important; min-width: 0; padding: 0 29px 0 8px !important; text-align: left !important; text-transform: none !important; }
.anomaly-filter-field:focus-within { border-color: var(--anomaly-primary); }
.anomaly-filter-field .el-input__icon { line-height: 38px !important; }
.anomaly-filter-field .el-select__caret { color: var(--anomaly-primary); }
.anomaly-year-filter { min-width: 130px; }
.anomaly-advanced-toggle.is-active { background: #fff3ef; border-color: #f8b49e; color: var(--anomaly-primary-dark); }
.anomaly-filter-count { align-items: center; background: var(--anomaly-primary); border-radius: 999px; color: #fff; display: inline-flex; font-size: 9px; font-weight: 700; height: 18px; justify-content: center; min-width: 18px; padding: 0 5px; }
.anomaly-view-toggle { align-items: center; background: #fff; border: 1px solid #d8dde5; border-radius: 8px; display: inline-flex; height: 40px; overflow: hidden; padding: 2px; }
.anomaly-view-toggle button { align-items: center; background: transparent; border: 0; border-radius: 6px; color: #778397; cursor: pointer; display: inline-flex; font-size: 10px; font-weight: 600; gap: 6px; height: 34px; padding: 0 10px; }
.anomaly-view-toggle button:hover { color: var(--anomaly-primary-dark); }
.anomaly-view-toggle button.is-active { background: var(--anomaly-primary); box-shadow: 0 3px 8px rgba(249, 99, 50, .22); color: #fff; }
.anomaly-icon-action { flex: 0 0 40px; height: 40px; min-width: 40px; padding: 0; width: 40px; }
.anomaly-icon-action:disabled { cursor: wait; opacity: .65; }

.anomaly-advanced-filters { border-top: 1px solid #edf0f4; display: grid; flex: 0 0 100%; gap: 9px; grid-template-columns: minmax(175px, 1fr) minmax(175px, 1fr) minmax(155px, .9fr) minmax(155px, .9fr) minmax(150px, .85fr) minmax(125px, .7fr) minmax(280px, 1.5fr); overflow-x: auto; padding: 11px 1px 4px; }
.anomaly-advanced-filter { min-width: 0; padding-top: 4px; position: relative; }
.anomaly-advanced-filter > label { background: #fff; color: #737f91; font-size: 9px; font-weight: 700; left: 9px; line-height: 1; margin: 0; padding: 0 4px; position: absolute; top: 0; text-transform: uppercase; z-index: 2; }
.anomaly-advanced-filter .el-select, .anomaly-advanced-filter .el-date-editor { width: 100% !important; }
.anomaly-list-page .anomaly-advanced-filter .el-input__inner,
.anomaly-list-page .anomaly-advanced-filter .el-range-editor.el-input__inner { background: #fff !important; border: 1px solid #d8dde5 !important; border-radius: 8px !important; box-shadow: none !important; color: #2c2c2c !important; font-size: 12px !important; height: 36px !important; line-height: 34px !important; margin: 0 !important; padding: 0 31px 0 11px !important; }
.anomaly-list-page .anomaly-advanced-filter .el-range-editor.el-input__inner { padding: 0 8px !important; }
.anomaly-list-page .anomaly-advanced-filter .el-input.is-focus .el-input__inner,
.anomaly-list-page .anomaly-advanced-filter .el-range-editor.is-active { border-color: var(--anomaly-primary) !important; }
.anomaly-list-page .anomaly-advanced-filter .el-input__icon,
.anomaly-list-page .anomaly-advanced-filter .el-range__icon,
.anomaly-list-page .anomaly-advanced-filter .el-range__close-icon { line-height: 34px !important; }
.anomaly-list-page .anomaly-advanced-filter .el-range-input { color: #2c2c2c; font-size: 12px; }
.anomaly-list-page .anomaly-advanced-filter .el-range-separator { color: #8b95a5; line-height: 30px; width: 24px; }
.anomaly-filter-popper { background: #fff !important; border: 1px solid #e1e5eb !important; border-radius: 8px !important; box-shadow: 0 12px 30px rgba(35, 48, 68, .16) !important; margin-top: 6px !important; z-index: 12050 !important; }
.el-picker-panel.anomaly-filter-popper, .anomaly-filter-popper .el-picker-panel__body, .anomaly-filter-popper .el-date-range-picker__content { background: #fff !important; opacity: 1 !important; }
.anomaly-filter-popper .el-select-dropdown__item { color: #465266; font-size: 12px; height: 34px; line-height: 34px; padding: 0 12px; }
.anomaly-filter-popper .el-select-dropdown__item.hover, .anomaly-filter-popper .el-select-dropdown__item:hover { background: #fff4f0; }
.anomaly-filter-popper .el-select-dropdown__item.selected { color: #f96332; font-weight: 700; }

.anomaly-table-shell { background: #fff; border: 1px solid var(--anomaly-border); border-radius: 12px; box-shadow: 0 6px 18px rgba(35, 48, 68, .045); margin-bottom: 14px; overflow: hidden; }
.anomaly-table-scroll { overflow-x: auto; }
.anomaly-table-scroll:focus { outline: 2px solid rgba(249, 99, 50, .32); outline-offset: -2px; }
.anomaly-table { border-collapse: separate; border-spacing: 0; color: #4b586d; font-size: 11px; margin: 0; min-width: 1260px; table-layout: fixed; width: 100%; }
.anomaly-table th { background: #f7f9fc; border-bottom: 1px solid #e5e9ef; color: #7d899a; font-size: 9px; font-weight: 700; letter-spacing: .045em; padding: 11px 12px; text-align: left; text-transform: uppercase; white-space: nowrap; }
.anomaly-table td { background: #fff; border-bottom: 1px solid #edf0f4; height: 66px; padding: 10px 12px; vertical-align: middle; }
.anomaly-table tbody tr:last-child td { border-bottom: 0; }
.anomaly-table tbody tr:hover td { background: #fffaf8; }
.anomaly-table th:nth-child(1) { width: 110px; }
.anomaly-table th:nth-child(2) { width: 105px; }
.anomaly-table th:nth-child(3) { width: 195px; }
.anomaly-table th:nth-child(4) { width: 135px; }
.anomaly-table th:nth-child(5) { width: 145px; }
.anomaly-table th:nth-child(6) { width: 145px; }
.anomaly-table th:nth-child(7) { width: 150px; }
.anomaly-table th:nth-child(8) { width: 235px; }
.anomaly-table th:nth-child(9) { width: 135px; }
.anomaly-table th:last-child,
.anomaly-table td:last-child { box-shadow: -8px 0 14px -14px rgba(35, 48, 68, .5); position: sticky; right: 0; }
.anomaly-table th:last-child { z-index: 3; }
.anomaly-table td:last-child { z-index: 2; }
.anomaly-table-reference button { background: none; border: 0; color: var(--anomaly-ink); cursor: pointer; display: block; font-size: 12px; font-weight: 700; padding: 0; }
.anomaly-table-reference button:hover { color: var(--anomaly-primary); }
.anomaly-table-reference small, .anomaly-table-product small, .anomaly-table-command small, .anomaly-table-date small { color: var(--anomaly-muted); display: block; font-size: 9px; margin-top: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.anomaly-table-product strong, .anomaly-table-command strong, .anomaly-table-date strong { color: #354258; display: block; font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.anomaly-table-description span { display: -webkit-box; line-height: 1.35; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.anomaly-table-actions { justify-content: flex-start !important; }

.anomaly-card-grid { margin-left: -7px; margin-right: -7px; }
.anomaly-card-column { display: flex; padding: 0 7px 14px; }
.anomaly-result-card { background: #fff; border: 1px solid var(--anomaly-border); border-radius: 12px; box-shadow: 0 6px 18px rgba(35, 48, 68, .045); display: flex; flex-direction: column; min-height: 420px; overflow: hidden; position: relative; width: 100%; }
.anomaly-result-card::before { background: var(--anomaly-primary); content: ''; height: 3px; left: 0; position: absolute; right: 0; top: 0; }
.anomaly-card-header { align-items: center; border-bottom: 1px solid #edf0f4; display: flex; gap: 10px; justify-content: space-between; min-height: 46px; padding: 12px 13px 9px; }
.anomaly-reference { background: none; border: 0; color: var(--anomaly-ink); cursor: pointer; font-size: 13px; font-weight: 700; padding: 0; }
.anomaly-reference:hover { color: var(--anomaly-primary); }
.anomaly-card-badges { align-items: center; display: flex; flex-wrap: wrap; gap: 5px; justify-content: flex-end; }
.anomaly-badge { align-items: center; border-radius: 999px; display: inline-flex; font-size: 9px; font-weight: 700; gap: 4px; min-height: 22px; padding: 4px 7px; white-space: nowrap; }
.anomaly-badge--open { background: #fff6de; color: #9a6b00; }
.anomaly-badge--closed { background: #eaf8ef; color: #237b45; }
.anomaly-badge--cost { background: #fff3ee; color: #be4f28; }
.anomaly-card-content { display: flex; flex: 1; flex-direction: column; padding: 14px 13px 10px; }
.anomaly-card-product { min-height: 50px; }
.anomaly-card-product h3 { color: var(--anomaly-ink); font-size: 15px; font-weight: 700; line-height: 1.25; margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.anomaly-overline { color: var(--anomaly-primary); display: block; font-size: 9px; font-weight: 700; margin-top: 3px; overflow: hidden; text-overflow: ellipsis; text-transform: uppercase; white-space: nowrap; }
.anomaly-card-facts { background: #f8f9fb; border: 1px solid #edf0f4; border-radius: 9px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); margin-top: 4px; overflow: hidden; }
.anomaly-card-fact { align-items: center; display: flex; gap: 8px; min-height: 50px; min-width: 0; padding: 8px 10px; }
.anomaly-card-fact + .anomaly-card-fact { border-left: 1px solid #e9edf2; }
.anomaly-card-fact--wide { border-bottom: 1px solid #e9edf2; grid-column: 1 / -1; }
.anomaly-card-fact--wide + .anomaly-card-fact { border-left: 0; }
.anomaly-fact-icon { align-items: center; background: #fff; border-radius: 7px; color: var(--anomaly-primary); display: inline-flex; flex: 0 0 28px; height: 28px; justify-content: center; }
.anomaly-card-fact > span:last-child { min-width: 0; }
.anomaly-card-fact small { color: #8b96a7; display: block; font-size: 8px; text-transform: uppercase; }
.anomaly-card-fact strong { color: #455166; display: block; font-size: 10px; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.anomaly-description-box { border-left: 2px solid var(--anomaly-primary); margin-top: 10px; min-height: 68px; padding: 8px 10px; }
.anomaly-description-box small { color: #8b96a7; display: block; font-size: 8px; font-weight: 700; text-transform: uppercase; }
.anomaly-description-box p { color: #566278; display: -webkit-box; font-size: 10px; line-height: 1.45; margin: 6px 0 0; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 3; }
.anomaly-card-history { border-top: 1px solid #edf0f4; display: grid; gap: 7px; grid-template-columns: repeat(2, minmax(0, 1fr)); margin-top: auto; padding-top: 10px; }
.anomaly-card-history > div { align-items: center; display: flex; gap: 7px; min-width: 0; }
.anomaly-card-history > div > span:last-child { min-width: 0; }
.anomaly-history-icon { align-items: center; border-radius: 50%; display: inline-flex; flex: 0 0 24px; height: 24px; justify-content: center; }
.anomaly-history-icon--created { background: #fff3ee; color: var(--anomaly-primary); }
.anomaly-history-icon--closed { background: #eaf8ef; color: #26834b; }
.anomaly-history-icon--department { background: #edf3ff; color: #3d6dcc; }
.anomaly-card-history small { color: #8b96a7; display: block; font-size: 8px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.anomaly-card-history strong { color: #455166; display: block; font-size: 9px; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.anomaly-card-footer { align-items: center; background: #fbfcfd; border-top: 1px solid #edf0f4; display: flex; justify-content: flex-end; min-height: 54px; padding: 9px 13px; }
.anomaly-card-actions { align-items: center; display: flex; gap: 7px; justify-content: flex-end; }
.anomaly-card-actions button { align-items: center; background: #fff; border: 1px solid #e5e9ef; border-radius: 7px; color: #657289; cursor: pointer; display: inline-flex; height: 32px; justify-content: center; transition: .16s ease; width: 32px; }
.anomaly-card-actions button:hover { background: #fff4f0; border-color: #f7b49f; color: var(--anomaly-primary-dark); }
.anomaly-card-actions button.anomaly-action--danger:hover { background: #fff0f0; border-color: #efb0b0; color: #c43d3d; }

.anomaly-state-card { align-items: center; background: #fff; border: 1px solid var(--anomaly-border); border-radius: 12px; color: var(--anomaly-muted); display: flex; flex-direction: column; justify-content: center; min-height: 260px; padding: 32px; text-align: center; }
.anomaly-state-card > span { align-items: center; background: #f3f5f8; border-radius: 50%; display: inline-flex; font-size: 20px; height: 52px; justify-content: center; margin-bottom: 13px; width: 52px; }
.anomaly-state-card h3 { color: var(--anomaly-ink); font-size: 16px; margin: 0 0 6px; }
.anomaly-state-card p { margin: 0 0 16px; }
.anomaly-state-card--error > span { background: #fff0f0; color: #c54242; }
.anomaly-skeleton { animation: anomaly-pulse 1.4s ease-in-out infinite; background: #edf0f4; border-radius: 5px; display: block; }
.anomaly-skeleton--reference { height: 13px; width: 78px; }
.anomaly-skeleton--badge { height: 20px; width: 72px; }
.anomaly-skeleton--title { height: 17px; margin-top: 4px; width: 74%; }
.anomaly-skeleton--subtitle { height: 10px; margin-top: 7px; width: 42%; }
.anomaly-skeleton--fact { height: 48px; margin-top: 10px; width: 100%; }
.anomaly-skeleton--description { height: 68px; margin-top: 10px; width: 100%; }
.anomaly-skeleton--action { height: 32px; margin-left: 7px; width: 32px; }
.anomaly-table-skeleton { height: 9px; width: 75%; }
.anomaly-result-card--skeleton::before { background: #dfe4ea; }
@keyframes anomaly-pulse { 0%, 100% { opacity: .55; } 50% { opacity: 1; } }

.anomaly-detail-dialog { border-radius: 14px; overflow: hidden; }
.anomaly-detail-dialog .el-dialog__header { border-bottom: 1px solid #edf0f4; padding: 18px 22px; }
.anomaly-detail-dialog .el-dialog__body { background: #f8f9fb; padding: 18px 22px; }
.anomaly-detail-dialog .el-dialog__footer { border-top: 1px solid #edf0f4; padding: 14px 22px; }
.anomaly-dialog-title { align-items: center; display: flex; gap: 12px; text-align: left; }
.anomaly-dialog-icon { align-items: center; background: #fff0ec; border-radius: 9px; color: var(--anomaly-primary); display: inline-flex; height: 38px; justify-content: center; width: 38px; }
.anomaly-dialog-title div { display: flex; flex-direction: column; }
.anomaly-dialog-title strong { color: var(--anomaly-ink); font-size: 16px; }
.anomaly-dialog-title small { color: var(--anomaly-muted); font-size: 11px; margin-top: 2px; }
.anomaly-detail-grid { display: grid; gap: 12px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.anomaly-detail-section { background: #fff; border: 1px solid #e6eaf0; border-radius: 10px; padding: 14px; }
.anomaly-detail-section--wide { grid-column: 1 / -1; }
.anomaly-detail-section h3 { color: var(--anomaly-ink); font-size: 12px; font-weight: 700; margin: 0 0 10px; text-transform: uppercase; }
.anomaly-detail-section dl { margin: 0; }
.anomaly-detail-section dl > div { display: grid; gap: 10px; grid-template-columns: 110px minmax(0, 1fr); padding: 5px 0; }
.anomaly-detail-section dt { color: var(--anomaly-muted); font-size: 10px; font-weight: 500; }
.anomaly-detail-section dd { color: #3e4a5e; font-size: 11px; margin: 0; overflow-wrap: anywhere; }
.anomaly-detail-long { gap: 3px !important; grid-template-columns: 1fr !important; }
.anomaly-dialog-footer { display: flex; gap: 8px; justify-content: flex-end; }
.anomaly-detail-dialog .el-button--primary { background: #f96332; border-color: #f96332; }

@media (max-width: 1399px) {
  .anomaly-toolbar-main { flex-basis: 100%; }
  .anomaly-toolbar-actions { margin-left: auto; }
}

@media (max-width: 991px) {
  .anomaly-summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .anomaly-detail-grid { grid-template-columns: 1fr; }
  .anomaly-detail-section--wide { grid-column: auto; }
}

@media (max-width: 767px) {
  .anomaly-summary-grid { gap: 9px; margin-bottom: 12px; }
  .anomaly-summary-card { min-height: 68px; padding: 11px; }
  .anomaly-summary-icon { flex-basis: 38px; height: 38px; width: 38px; }
  .anomaly-summary-card strong { font-size: 18px; }
  .anomaly-toolbar-card { gap: 10px; padding: 10px; }
  .anomaly-toolbar-main { flex-wrap: wrap; }
  .anomaly-search-field { flex-basis: 100%; }
  .anomaly-filter-field { flex: 1; min-width: 140px; }
  .anomaly-toolbar-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); margin: 0; width: 100%; }
  .anomaly-toolbar-actions > .anomaly-secondary-action:not(.anomaly-icon-action) { width: 100%; }
  .anomaly-view-toggle { grid-column: 1 / -1; width: 100%; }
  .anomaly-view-toggle button { flex: 1; justify-content: center; }
  .anomaly-icon-action { width: 100%; }
  .anomaly-advanced-filters { grid-template-columns: minmax(210px, 1fr); overflow: visible; }
  .anomaly-card-history { grid-template-columns: 1fr; }
  .anomaly-detail-dialog { width: 94% !important; }
}

@media (max-width: 480px) {
  .anomaly-summary-grid { grid-template-columns: 1fr; }
  .anomaly-filter-field { flex-basis: 100%; }
}
</style>
